import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ListFilterPlus, X } from 'lucide-react';
import { NotFoundContainer, ErrorMessage } from '@/components/ui';
import { CancelSessionModal } from '@/components/ui/CancelSessionModal';
import { cn } from '@/lib/utils';
import { useProfessionalCalendar } from '@/context/ProfessionalCalendarContext';
import type { ProfessionalHistoryDateRange } from '@/context/ProfessionalCalendarContext';
import { groupSessionsByDate } from '@/pages/professional/history/utils/groupSessionsByDate';
import { ProfessionalHistorySessionCard } from './ProfessionalHistorySessionCard';
import { ProfessionalSessionModal } from './ProfessionalSessionModal';
import { getSessionPatientDisplayName } from '../utils/getSessionPatientDisplayName';
import { useCancelProfessionalSession } from '@/services';

type ProfessionalHistoryPanelProps = {
	sessions: Sessions.Session[];
	allSessions: Sessions.Session[];
	isLoading: boolean;
	isError: boolean;
};

type StatusFilterOption = {
	value: Sessions.SessionStatus | null;
	label: string;
};

const statusOptions: StatusFilterOption[] = [
	{ value: null, label: 'Todas' },
	{ value: 'CREATED', label: 'A pagar' },
	{ value: 'CONFIRMED', label: 'Pagas' },
	{ value: 'STARTED', label: 'Em andamento' },
	{ value: 'FINISHED', label: 'Finalizadas' },
	{ value: 'CANCELLED', label: 'Canceladas' },
];

const buildStatusCounts = (sessions: Sessions.Session[]) => {
	const counts: Record<Sessions.SessionStatus, number> = {
		CREATED: 0,
		CONFIRMED: 0,
		STARTED: 0,
		FINISHED: 0,
		CANCELLED: 0,
	};

	for (const session of sessions) {
		counts[session.status] += 1;
	}

	return {
		all: sessions.length,
		...counts,
	};
};

const normalizeHistoryDateRange = (
	range: ProfessionalHistoryDateRange,
): ProfessionalHistoryDateRange => {
	const { start, end } = range;
	if (!start || !end || start <= end) return range;
	return { start: end, end: start };
};

const historyDateInputClassName =
	'w-full min-w-0 rounded-xl border border-stone-200 bg-white px-2.5 py-2 text-[11px] text-stone-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300';

const ProfessionalHistoryPanel = ({
	sessions,
	allSessions,
	isLoading,
	isError,
}: ProfessionalHistoryPanelProps) => {
	const {
		view,
		now,
		isHistoryPanelOpen,
		setHistoryPanelOpen,
		historyStatusFilter,
		setHistoryStatusFilter,
		historyDateRange,
		setHistoryDateRange,
	} = useProfessionalCalendar();

	const hasDateRangeFilter = Boolean(
		historyDateRange.start || historyDateRange.end,
	);

	const handleDateRangeFieldChange = (
		field: 'start' | 'end',
		value: string,
	) => {
		const nextRange = normalizeHistoryDateRange({
			...historyDateRange,
			[field]: value || null,
		});
		setHistoryDateRange(nextRange);
	};

	const clearDateRangeFilter = () => {
		setHistoryDateRange({ start: null, end: null });
	};

	const groupedSessions = groupSessionsByDate(sessions, now);
	const statusCounts = useMemo(
		() => buildStatusCounts(allSessions),
		[allSessions],
	);
	const [selectedSession, setSelectedSession] =
		useState<Sessions.Session | null>(null);
	const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
	const [sessionToCancel, setSessionToCancel] =
		useState<Sessions.Session | null>(null);

	const { mutate: cancelSession, isPending: isCancellingSession } =
		useCancelProfessionalSession(() => {
			setSessionToCancel(null);
			setIsSessionModalOpen(false);
			setSelectedSession(null);
		});

	const openSessionModal = (session: Sessions.Session) => {
		setSelectedSession(session);
		setIsSessionModalOpen(true);
	};

	useEffect(() => {
		if (!isHistoryPanelOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setHistoryPanelOpen(false);
		};

		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.body.style.overflow = 'auto';
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isHistoryPanelOpen, setHistoryPanelOpen]);

	return (
		<>
			{isHistoryPanelOpen
				? createPortal(
						<div
							className="fixed inset-0 z-[9990] flex items-center justify-center p-4 md:p-6"
							role="dialog"
							aria-modal="true"
							aria-label="Filtros e histórico"
						>
							<div
								className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
								aria-hidden
								onClick={() => setHistoryPanelOpen(false)}
							/>

							<div
								className="relative flex h-[min(85vh,720px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl"
								onClick={(event) => event.stopPropagation()}
							>
								<div className="relative shrink-0 space-y-4 border-b border-stone-200 px-4 pb-4 pt-3">
									<button
										type="button"
										onClick={() =>
											setHistoryPanelOpen(false)
										}
										className="absolute right-2 top-2 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
										aria-label="Fechar filtros"
									>
										<X size={20} />
									</button>

									<div className="space-y-2 pr-8">
										<div className="flex items-center gap-2 text-stone-800">
											<Calendar className="size-4 shrink-0" />
											<span className="text-[10px] font-black uppercase tracking-widest">
												Período
											</span>
										</div>
										<div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
											<input
												type="date"
												value={
													historyDateRange.start ?? ''
												}
												onChange={(event) =>
													handleDateRangeFieldChange(
														'start',
														event.target.value,
													)
												}
												className={
													historyDateInputClassName
												}
												aria-label="Data inicial"
											/>
											<span className="text-[11px] font-medium text-stone-500">
												até
											</span>
											<input
												type="date"
												value={
													historyDateRange.end ?? ''
												}
												onChange={(event) =>
													handleDateRangeFieldChange(
														'end',
														event.target.value,
													)
												}
												className={
													historyDateInputClassName
												}
												aria-label="Data final"
											/>
										</div>
										{hasDateRangeFilter ? (
											<button
												type="button"
												onClick={clearDateRangeFilter}
												className="text-[11px] font-bold text-stone-400 transition-colors hover:text-stone-700"
											>
												Limpar período
											</button>
										) : null}
									</div>

									<div className="space-y-2">
										<div className="flex items-center gap-2 text-stone-800">
											<ListFilterPlus className="size-4 shrink-0" />
											<span className="text-[10px] font-black uppercase tracking-widest">
												Status
											</span>
										</div>

										<div
											id="history-status-filters"
											className="grid grid-cols-3 gap-2"
											role="radiogroup"
										>
											{statusOptions.map((option) => {
												const count =
													option.value === null
														? statusCounts.all
														: statusCounts[
																option.value
														  ];
												const isSelected =
													historyStatusFilter ===
													option.value;

												return (
													<button
														key={option.label}
														type="button"
														role="radio"
														aria-checked={
															isSelected
														}
														aria-label={`${option.label}, ${count}`}
														onClick={() =>
															setHistoryStatusFilter(
																option.value,
															)
														}
														className={cn(
															'flex items-center justify-between gap-1 rounded-xl border px-2.5 py-2 text-left text-[11px] leading-tight transition-colors focus:outline-none',
															isSelected
																? 'border-stone-600 bg-white font-bold text-stone-950 shadow-sm'
																: 'border-stone-200 bg-white font-medium text-stone-500 hover:border-stone-300 hover:bg-stone-50',
														)}
													>
														<span className="min-w-0 truncate">
															{option.label}
														</span>
														<span
															className={cn(
																'shrink-0 tabular-nums',
																isSelected
																	? 'text-stone-900'
																	: 'text-stone-400',
															)}
														>
															{count}
														</span>
													</button>
												);
											})}
										</div>
									</div>
								</div>

								<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4">
									{isLoading ? (
										<div className="flex flex-1 items-center justify-center py-12 text-center text-xs font-bold uppercase tracking-widest text-zinc-400">
											Carregando histórico...
										</div>
									) : isError ? (
										<div className="flex flex-1 items-center justify-center py-12">
											<ErrorMessage error="Erro ao carregar histórico" />
										</div>
									) : groupedSessions.length === 0 ? (
										<div className="flex flex-1 items-center justify-center py-12">
											<NotFoundContainer
												title="Nenhum resultado"
												message="Nenhuma consulta com os filtros atuais."
											/>
										</div>
									) : (
										<div className="space-y-6">
											{groupedSessions.map((group) => (
												<div
													key={group.label}
													className="space-y-2"
												>
													<h3
														className={cn(
															'text-sm font-black text-zinc-800',
															view === 'MONTH' &&
																group.label ===
																	'Hoje' &&
																'text-base',
														)}
													>
														{group.label}
													</h3>
													<div className="space-y-2">
														{group.sessions.map(
															(session) => (
																<ProfessionalHistorySessionCard
																	key={
																		session.id
																	}
																	session={
																		session
																	}
																	onOpen={() =>
																		openSessionModal(
																			session,
																		)
																	}
																	onCancelRequest={() =>
																		setSessionToCancel(
																			session,
																		)
																	}
																/>
															),
														)}
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</div>,
						document.body,
				  )
				: null}

			<ProfessionalSessionModal
				session={selectedSession}
				isOpen={isSessionModalOpen}
				onClose={() => {
					setIsSessionModalOpen(false);
					setSelectedSession(null);
				}}
			/>

			<CancelSessionModal
				isOpen={sessionToCancel != null}
				onClose={() => setSessionToCancel(null)}
				onConfirm={(reason) => {
					if (!sessionToCancel || isCancellingSession) return;
					cancelSession({ id: sessionToCancel.id, reason });
				}}
				session={sessionToCancel}
				actor="professional"
				counterpartName={
					sessionToCancel
						? getSessionPatientDisplayName(sessionToCancel)
						: undefined
				}
				isPending={isCancellingSession}
			/>
		</>
	);
};

export { ProfessionalHistoryPanel };
